import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import {
  LANGUAGE,
  CURRENT_AFFILIATE,
  WHITE_LABEL_AFFILIATE,
  getItemFromLocalStorage,
  getTokenFromLocalStorage,
  removeTokenFromLocalStorage,
  getTemporaryTokenFromLocalStorage
} from './local-storage';
import { Urls } from 'shared/constants/urls';
import { pathToFileURL } from 'url';

const defaultConfig: AxiosRequestConfig = {
  baseURL: process.env.REACT_APP_BASE_API_URL,
  timeout: 60000,
  headers: {
    'content-type': 'application/json'
  }
};

export const instance = axios.create(defaultConfig);

// Track in-flight upload requests to prevent token clearing during uploads
const inFlightUploads = new Set<string>();

instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    if (config.headers?.Authorization) {
      return config;
    }
    const token = getTokenFromLocalStorage();
    const temporaryToken = getTemporaryTokenFromLocalStorage();
    const language = getItemFromLocalStorage(LANGUAGE);
    const affiliateId =
      getItemFromLocalStorage(WHITE_LABEL_AFFILIATE) ||
      getItemFromLocalStorage(CURRENT_AFFILIATE);
    const output = {
      ...config,
      headers: { ...config.headers }
    };
    if (temporaryToken) {
      output.headers.Authorization = `Bearer ${temporaryToken}`;
    } else if (token) {
      output.headers.Authorization = `Bearer ${token}`;
    }
    output.headers['accept-language'] = language;
    if (affiliateId) {
      output.headers['x-affiliate-id'] = affiliateId;
    }

    // Track upload requests (answerLocation endpoint)
    if (config.url?.includes('answerLocation')) {
      const requestId = `${config.method}-${config.url}-${Date.now()}`;
      inFlightUploads.add(requestId);
      // Store request ID on config for cleanup
      (config as any).__uploadRequestId = requestId;
    }

    return output;
  },
  (error) => Promise.reject(error)
);

instance.interceptors.response.use(
  (response) => {
    // Clean up upload tracking on success
    const requestId = (response.config as any)?.__uploadRequestId;
    if (requestId) {
      inFlightUploads.delete(requestId);
    }
    return response;
  },
  (error) => {
    const path = window.location.pathname;
    const search = window.location.search;
    const requestId = (error?.config as any)?.__uploadRequestId;

    // Clean up upload tracking on error
    if (requestId) {
      inFlightUploads.delete(requestId);
    }

    if (error?.response?.status === 401 && path !== '/login') {
      const failedUrl = error?.config?.url || '';
      const isUploadRequest = failedUrl?.includes('answerLocation');
      const isNonCriticalRequest =
        failedUrl?.includes('getMyUnreadCount') ||
        failedUrl?.includes('patientConversations');

      // Check if we're in the Online Visit System (OVS) flow
      const isOVSPath =
        path.includes('/startVisit') ||
        path.includes('/onlineVisit') ||
        path.includes('/completeQuestionnaire') ||
        path.includes('/orderPayment') ||
        path.includes('/affiliate/externalVisit');

      // Don't clear token if:
      // 1. There are uploads in flight (race condition protection)
      // 2. The failed request is a non-critical background request
      // 3. The failed request itself is an upload (let it fail gracefully)
      // 4. We're in the OVS flow (preserve token to avoid interrupting the visit)
      const shouldPreserveToken =
        inFlightUploads.size > 0 ||
        isNonCriticalRequest ||
        isUploadRequest ||
        isOVSPath;

      if (!shouldPreserveToken) {
        removeTokenFromLocalStorage();
        let href: string = Urls.LoginPage;
        if (path.includes('/startVisit') || path.includes('/onlineVisit')) {
          href += `?redirect=${encodeURIComponent(path + search)}`;
        }
        window.location.href = href;
      }
    }
    return Promise.reject(error);
  }
);

export const http = async <T>({
  url,
  method = 'GET',
  params,
  data,
  ...rest
}: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
  instance({ url, method, params, data, ...rest });

export const setupToken = (token: string) => ({
  headers: {
    Authorization: `Bearer ${token}`
  }
});
